import { useCallback, useState } from 'react';

/**
 * `useState` that survives a reload, stored JSON-encoded under `key`.
 *
 * Storage can be unavailable — a private window, blocked site data — so every
 * access is guarded and the state still works, in memory only, for the session.
 */
export function useStoredState<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Keep it in memory only.
      }
    },
    [key],
  );

  return [value, set];
}
